/**
 * Whisper of the Grove — slow ticker under the Spark.
 *
 * Cycles through context-appropriate whispers from `@sai-clicker/content`.
 * Categories: sai voice, grove observations, community aggregates, context.
 *
 * For the alpha we just rotate through every whisper at a fixed cadence.
 * Future: filter by `condition` against current state, weight by category.
 */
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { WHISPERS_ALPHA } from '@sai-clicker/content';
import { useAnchor } from '../onboarding/useAnchor.js';
import styles from './WhisperTicker.module.css';

const ROTATION_INTERVAL_MS = 8_000;

// Filter out templated community whispers from the alpha pool until
// we have real numbers to substitute. Keeping them would show literal
// "{playersToday}" to the player.
const ELIGIBLE_WHISPERS = WHISPERS_ALPHA.filter((w) => !/\{[a-zA-Z]+\}/.test(w.text));

export function WhisperTicker(): JSX.Element | null {
  const [index, setIndex] = useState(0);
  // Регистрируем ticker как onboarding anchor — подсказка про шёпот
  // выпадает прямо над его текстом.
  const rootRef = useAnchor<HTMLDivElement>('whisper');

  useEffect(() => {
    if (ELIGIBLE_WHISPERS.length === 0) {
      return undefined;
    }
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % ELIGIBLE_WHISPERS.length);
    }, ROTATION_INTERVAL_MS);
    return () => {
      window.clearInterval(id);
    };
  }, []);

  if (ELIGIBLE_WHISPERS.length === 0) {
    return null;
  }

  const current = ELIGIBLE_WHISPERS[index] ?? ELIGIBLE_WHISPERS[0];
  if (!current) {
    return null;
  }

  return (
    <div ref={rootRef} className={styles.root} aria-live="polite">
      <AnimatePresence mode="wait">
        <motion.p
          key={current.id}
          className={styles.text}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          {current.text}
        </motion.p>
      </AnimatePresence>
    </div>
  );
}
